import * as React from 'react';
import { useContext, useEffect, useRef } from 'react';
import {
  useNavigate as useReactRouterNavigate,
  useLocation,
  useParams,
  useBlocker,
  useMatch,
  useInRouterContext,
  Link,
  Navigate,
  Route,
  Routes,
  Outlet,
  matchPath,
  createBrowserRouter,
  RouterProvider as ReactRouterProvider,
  UNSAFE_DataRouterContext,
  UNSAFE_DataRouterStateContext,
} from 'react-router-dom';
import type { RouterProvider } from 'ra-core';

const routerProviderFuture = {
  v7_startTransition: false,
  v7_relativeSplatPath: false,
};

const useCanBlock = () => {
  const dataRouterContext = useContext(UNSAFE_DataRouterContext);
  const dataRouterStateContext = useContext(UNSAFE_DataRouterStateContext);
  return !!(dataRouterContext && dataRouterStateContext);
};

const useNavigate = () => {
  const navigate = useReactRouterNavigate();
  const navigateRef = useRef(navigate);
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);
  return React.useCallback((...args: Parameters<typeof navigate>) => {
    return navigateRef.current(...args);
  }, []);
};

const InternalRouter = ({ children, basename }: { children: React.ReactNode; basename?: string }) => {
  const router = createBrowserRouter(
    [{ path: '*', element: React.createElement(React.Fragment, null, children) }],
    {
      basename,
      future: {
        v7_fetcherPersist: false,
        v7_normalizeFormMethod: false,
        v7_partialHydration: false,
        v7_relativeSplatPath: false,
        v7_skipActionErrorRevalidation: false,
      },
    },
  );
  return React.createElement(ReactRouterProvider as any, {
    router,
    future: routerProviderFuture,
  });
};

const RouterWrapper = ({ basename, children }: { basename?: string; children: React.ReactNode }) => {
  const isInRouter = useInRouterContext();
  if (isInRouter) {
    return React.createElement(React.Fragment, null, children);
  }
  return React.createElement(InternalRouter, { basename, children });
};

export const browserRouterProvider: RouterProvider = {
  useNavigate: useNavigate as RouterProvider['useNavigate'],
  useLocation: useLocation as RouterProvider['useLocation'],
  useParams: useParams as RouterProvider['useParams'],
  useBlocker: useBlocker as RouterProvider['useBlocker'],
  useMatch: useMatch as RouterProvider['useMatch'],
  useInRouterContext: useInRouterContext as RouterProvider['useInRouterContext'],
  useCanBlock,
  Link: Link as RouterProvider['Link'],
  Navigate: Navigate as RouterProvider['Navigate'],
  Route: Route as RouterProvider['Route'],
  Routes: Routes as RouterProvider['Routes'],
  Outlet: Outlet as RouterProvider['Outlet'],
  RouterWrapper: RouterWrapper as RouterProvider['RouterWrapper'],
  matchPath: matchPath as RouterProvider['matchPath'],
};
